import type {
    ChangeEvent,
    CSSProperties,
    FocusEvent,
    KeyboardEvent,
} from 'react';
import * as RadixSlider from '@radix-ui/react-slider';
import { DotsSixVerticalIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

interface SliderProps {
    value: { min: number; max: number };
    onChange: (next: { min: number; max: number }) => void;
    onChangeCommitted?: (next: { min: number; max: number }) => void;
    domainMinimum?: number;
    domainMaximum?: number;
    step?: number;
    minimumFixedAt?: number;
    maximumFixedAt?: number;
    tickValues?: number[];
    minimumInputLabel: string;
    maximumInputLabel: string;
    valueSuffix?: string;
    minimumThumbAriaLabel: string;
    maximumThumbAriaLabel: string;
}

const SNAP_POINT_COUNT = 13;

function clampToDomain(
    value: number,
    domainMinimum: number,
    domainMaximum: number,
): number {
    return Math.max(domainMinimum, Math.min(value, domainMaximum));
}

export function Slider({
    value,
    onChange,
    onChangeCommitted,
    domainMinimum = 0,
    domainMaximum = 100,
    step = 1,
    minimumFixedAt,
    maximumFixedAt,
    tickValues = [0, 25, 50, 75, 100],
    minimumInputLabel,
    maximumInputLabel,
    valueSuffix = '%',
    minimumThumbAriaLabel,
    maximumThumbAriaLabel,
}: SliderProps) {
    const normalizeSliderValues = (values: number[]) => {
        const rawMin = values[0];
        const rawMax = values[1];
        const nextMin = minimumFixedAt ?? rawMin;
        const nextMax = maximumFixedAt ?? rawMax;
        const orderedMin = Math.min(nextMin, nextMax);
        const orderedMax = Math.max(nextMin, nextMax);
        const clampedMin = clampToDomain(orderedMin, domainMinimum, orderedMax);
        const clampedMax = clampToDomain(orderedMax, clampedMin, domainMaximum);
        return { min: clampedMin, max: clampedMax };
    };

    const handleSliderChange = (values: number[]) => {
        onChange(normalizeSliderValues(values));
    };

    const handleSliderCommit = (values: number[]) => {
        onChangeCommitted?.(normalizeSliderValues(values));
    };

    const handleMinimumInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const raw = Number.parseInt(event.target.value, 10);
        const parsed = Number.isNaN(raw) ? domainMinimum : raw;
        const upperBound = maximumFixedAt ?? value.max;
        const nextMin = clampToDomain(parsed, domainMinimum, upperBound);
        onChange({ ...value, min: nextMin });
    };

    const handleMaximumInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        const raw = Number.parseInt(event.target.value, 10);
        const parsed = Number.isNaN(raw) ? domainMaximum : raw;
        const lowerBound = minimumFixedAt ?? value.min;
        const nextMax = clampToDomain(parsed, lowerBound, domainMaximum);
        onChange({ ...value, max: nextMax });
    };

    const commitInputChange = (event: FocusEvent<HTMLDivElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
            onChangeCommitted?.(value);
        }
    };

    const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.currentTarget.blur();
        }
    };

    const isMinimumLocked = minimumFixedAt !== undefined;
    const isMaximumLocked = maximumFixedAt !== undefined;

    const inputClassName =
        'h-8 w-[72px] rounded-[2px] border border-grey-500 bg-grey-600 px-1.5 font-geist-mono text-xs text-white-100 focus:outline-none focus:border-grey-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';

    const span = domainMaximum - domainMinimum;
    const positionPercentOf = (tick: number) =>
        span === 0 ? 0 : ((tick - domainMinimum) / span) * 100;

    const snapPositions = Array.from(
        { length: SNAP_POINT_COUNT },
        (_, index) => (index / (SNAP_POINT_COUNT - 1)) * 100,
    );

    const thumbConfigs = [
        { isLocked: isMinimumLocked, ariaLabel: minimumThumbAriaLabel },
        { isLocked: isMaximumLocked, ariaLabel: maximumThumbAriaLabel },
    ];

    return (
        <div className="flex flex-col gap-4 pt-2">
            <div className="flex flex-col gap-3">
                <RadixSlider.Root
                    className="relative flex h-[18px] w-full touch-none select-none items-center"
                    value={[value.min, value.max]}
                    onValueChange={handleSliderChange}
                    onValueCommit={handleSliderCommit}
                    max={domainMaximum}
                    min={domainMinimum}
                    step={step}
                    minStepsBetweenThumbs={0}
                >
                    <RadixSlider.Track className="relative h-3 grow bg-grey-550">
                        <div className="absolute inset-y-0 left-2 right-2">
                            {snapPositions.map((positionPercent) => (
                                <div
                                    key={positionPercent}
                                    className="absolute top-1/2 size-1 -translate-x-1/2 -translate-y-1/2 bg-grey-500 left-[var(--pos)]"
                                    style={
                                        {
                                            '--pos': `${positionPercent}%`,
                                        } as CSSProperties
                                    }
                                />
                            ))}
                        </div>
                        <RadixSlider.Range className="absolute h-full rounded-[1px] bg-red-bright" />
                    </RadixSlider.Track>
                    {thumbConfigs.map(({ isLocked, ariaLabel }) => (
                        <RadixSlider.Thumb
                            key={ariaLabel}
                            className={cn(
                                'flex size-[18px] shrink-0 cursor-pointer items-center justify-center rounded-[2px] bg-white-100 shadow-dropdown-medium focus:outline-none',
                                isLocked
                                    ? 'pointer-events-none cursor-default'
                                    : 'relative z-10',
                            )}
                            aria-label={ariaLabel}
                            tabIndex={isLocked ? -1 : undefined}
                        >
                            <DotsSixVerticalIcon
                                className={cn(
                                    'size-2.5',
                                    isLocked
                                        ? 'text-grey-300'
                                        : 'text-black-100',
                                )}
                            />
                        </RadixSlider.Thumb>
                    ))}
                </RadixSlider.Root>
                <div className="relative h-[18px] w-full">
                    {tickValues.map((tick) => {
                        const positionPercent = positionPercentOf(tick);
                        const edgeAlignment =
                            positionPercent === 0
                                ? 'left-0'
                                : positionPercent === 100
                                  ? 'right-0'
                                  : '-translate-x-1/2 left-[var(--pos)]';
                        return (
                            <span
                                key={`label-${tick}`}
                                className={cn(
                                    'absolute font-geist-mono text-xs text-grey-300',
                                    edgeAlignment,
                                )}
                                style={
                                    {
                                        '--pos': `${positionPercent}%`,
                                    } as CSSProperties
                                }
                            >
                                {tick}
                            </span>
                        );
                    })}
                </div>
            </div>
            <div
                className="flex items-center justify-between gap-2"
                onBlur={commitInputChange}
            >
                <div className="flex items-center gap-2">
                    <label className="text-sm-medium text-grey-200">
                        {minimumInputLabel}
                    </label>
                    <div className="relative">
                        <input
                            type="number"
                            min={domainMinimum}
                            max={value.max}
                            value={value.min}
                            disabled={isMinimumLocked}
                            onChange={handleMinimumInputChange}
                            onKeyDown={handleInputKeyDown}
                            className={cn(
                                inputClassName,
                                isMinimumLocked &&
                                    'cursor-not-allowed opacity-60',
                            )}
                        />
                        <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 font-geist-mono text-xs text-grey-300">
                            {valueSuffix}
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-sm-medium text-grey-200">
                        {maximumInputLabel}
                    </label>
                    <div className="relative">
                        <input
                            type="number"
                            min={value.min}
                            max={domainMaximum}
                            value={value.max}
                            disabled={isMaximumLocked}
                            onChange={handleMaximumInputChange}
                            onKeyDown={handleInputKeyDown}
                            className={cn(
                                inputClassName,
                                isMaximumLocked &&
                                    'cursor-not-allowed opacity-60',
                            )}
                        />
                        <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 font-geist-mono text-xs text-grey-300">
                            {valueSuffix}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}
